import { supabase } from '../lib/supabase';

/**
 * Tenant-row reads and writes (branding, settings, identity, publish state)
 * for tenant admins. RLS scopes updates to the caller's own tenant.
 */
export const tenantService = {
  /** Look up a tenant's slug by id. Returns null when not found. */
  async getTenantSlug(tenantId: string): Promise<string | null> {
    const { data, error } = await supabase
      .from('tenants')
      .select('slug')
      .eq('id', tenantId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return data?.slug ?? null;
  },

  /** Overwrite the tenant's branding JSONB (caller merges; this writes whole object). */
  async updateBranding(tenantId: string, branding: Record<string, unknown>): Promise<void> {
    const { error } = await supabase
      .from('tenants')
      .update({ branding, updated_at: new Date().toISOString() })
      .eq('id', tenantId);
    if (error) throw new Error(error.message);
  },

  /** Overwrite the tenant's settings JSONB (caller merges; this writes whole object). */
  async updateSettings(tenantId: string, settings: Record<string, unknown>): Promise<void> {
    const { error } = await supabase
      .from('tenants')
      .update({ settings, updated_at: new Date().toISOString() })
      .eq('id', tenantId);
    if (error) throw new Error(error.message);
  },

  /** Update the tenant's public name and logos. */
  async updateIdentity(
    tenantId: string,
    identity: { name: string; logoUrl: string | null; logoUrlLight: string | null },
  ): Promise<void> {
    const { error } = await supabase
      .from('tenants')
      .update({
        name: identity.name,
        logo_url: identity.logoUrl,
        logo_url_light: identity.logoUrlLight,
        updated_at: new Date().toISOString(),
      })
      .eq('id', tenantId);
    if (error) throw new Error(error.message);
  },

  /** Set whether the public marketing site is live. */
  async setSitePublished(tenantId: string, isPublished: boolean): Promise<void> {
    const { error } = await supabase
      .from('tenants')
      .update({ is_published: isPublished, updated_at: new Date().toISOString() })
      .eq('id', tenantId);
    if (error) throw new Error(error.message);
  },

  /** Flip the public marketing site live. */
  async publishSite(tenantId: string): Promise<void> {
    return this.setSitePublished(tenantId, true);
  },
};
